import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { apiService } from '../services/apiService'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Area, AreaChart
} from 'recharts'
import PendingApprovalsWidget from '../components/dashboard/PendingApprovalsWidget'
import CreditMetricsWidget from '../components/dashboard/CreditMetricsWidget'
import {
  exportToExcel,
  exportMultiSheetExcel,
  exportToPDF,
  exportChartAsImage,
  exportFullDashboard,
  getFilenameWithTimestamp,
  showExportNotification
} from '../utils/exportUtils'

const DashboardPage = () => {
  const user = useAuth()
  
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedPeriod, setSelectedPeriod] = useState('30d')
  const [activeTab, setActiveTab] = useState('principal')

  // Estados para Filtros Avançados
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [filters, setFilters] = useState({
    dateStart: '',
    dateEnd: '',
    clientType: 'all', // all, pf, pj
    contractStatus: 'all', // all, active, inactive, expired
    vehicleId: 'all',
    minRevenue: '',
    maxRevenue: ''
  })

  // Estados para Comparações Temporais
  const [showComparison, setShowComparison] = useState(true)
  const [previousPeriodData, setPreviousPeriodData] = useState(null)

  // Estados para Alertas Inteligentes
  const [intelligentAlerts, setIntelligentAlerts] = useState([])
  const [showAlerts, setShowAlerts] = useState(true)
  const [thresholds, setThresholds] = useState({
    vehicleUtilizationMin: 70, // Alerta se < 70%
    contractsMin: 5, // Alerta se < 5 contratos
    revenueDropPercent: -10, // Alerta se queda > 10%
    clientScoreMin: 60 // Alerta se score médio < 60
  })

  // Estados para Benchmarking
  const [showBenchmarking, setShowBenchmarking] = useState(true)
  const [benchmarkData, setBenchmarkData] = useState(null)

  useEffect(() => {
    loadDashboardData()
  }, [selectedPeriod])

  useEffect(() => {
    if (dashboardData) {
      checkIntelligentAlerts()
    }
  }, [dashboardData, thresholds])

  useEffect(() => {
    if (dashboardData) {
      calculateBenchmarks()
    }
  }, [dashboardData])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const response = await apiService.getDashboardData()
      setDashboardData(response)
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error)
    } finally {
      setLoading(false)
    }
  }

  // Funções de Exportação
  const handleExportExcel = () => {
    if (!dashboardData) {
      alert('Carregue os dados primeiro!')
      return
    }

    const sheets = []

    // Sheet 1: KPIs Principais
    if (dashboardData.kpis) {
      const kpisData = Object.keys(dashboardData.kpis).map(key => ({
        'Métrica': key.replace(/_/g, ' ').toUpperCase(),
        'Valor': dashboardData.kpis[key]
      }))
      sheets.push({ data: kpisData, sheetName: 'KPIs Principais' })
    }

    // Sheet 2: Top Clientes
    if (dashboardData.top_clients && dashboardData.top_clients.length > 0) {
      sheets.push({
        data: dashboardData.top_clients.map(c => ({
          'Cliente': c.name,
          'Receita Total': c.total_revenue,
          'Contratos Ativos': c.active_contracts,
          'Score de Crédito': c.credit_score
        })),
        sheetName: 'Top Clientes'
      })
    }

    // Sheet 3: Receita por Período
    if (dashboardData.revenue_by_period && dashboardData.revenue_by_period.length > 0) {
      sheets.push({
        data: dashboardData.revenue_by_period.map(r => ({
          'Período': r.period,
          'Receita': r.revenue,
          'Despesas': r.expenses,
          'Lucro': r.profit
        })),
        sheetName: 'Receita por Período'
      })
    }

    // Sheet 4: Analytics
    if (dashboardData.analytics) {
      const analyticsData = Object.keys(dashboardData.analytics).map(key => ({
        'Métrica': key.replace(/_/g, ' ').toUpperCase(),
        'Valor': dashboardData.analytics[key]
      }))
      sheets.push({ data: analyticsData, sheetName: 'Analytics' })
    }

    const filename = getFilenameWithTimestamp('dashboard_viploc')
    const result = exportMultiSheetExcel(sheets, filename)
    showExportNotification(result, 'Excel')
  }

  const handleExportPDF = async () => {
    const filename = getFilenameWithTimestamp('dashboard_viploc')
    const result = await exportToPDF('dashboard-container', filename, {
      header: 'VIPLOC System - Dashboard BI',
      footer: `Gerado em: ${new Date().toLocaleString('pt-BR')} | Período: ${selectedPeriod}`,
      orientation: 'landscape'
    })
    showExportNotification(result, 'PDF')
  }

  const handleExportComplete = async () => {
    if (!dashboardData) {
      alert('Carregue os dados primeiro!')
      return
    }

    const filename = getFilenameWithTimestamp('dashboard_viploc')
    const result = await exportFullDashboard(dashboardData, filename, 'both')

    if (result.success) {
      alert('✅ Dashboard completo exportado! Foram gerados 2 arquivos:\n- Excel com dados detalhados\n- PDF com visualização completa')
    } else {
      alert(`❌ Erro ao exportar: ${result.error}`)
    }
  }

  // Funções de Filtros Avançados
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const clearFilters = () => {
    setFilters({
      dateStart: '',
      dateEnd: '',
      clientType: 'all',
      contractStatus: 'all',
      vehicleId: 'all',
      minRevenue: '',
      maxRevenue: ''
    })
  }

  const applyFilters = () => {
    // Recarregar dados com filtros aplicados
    loadDashboardData()
    setShowAdvancedFilters(false)
  }

  const getFilteredData = (data) => {
    if (!data) return data

    let filtered = { ...data }

    // Filtrar clientes por tipo
    if (filters.clientType !== 'all' && filtered.top_clients) {
      filtered.top_clients = filtered.top_clients.filter(client =>
        filters.clientType === 'pf' ? client.cpf_cnpj?.length <= 14 : client.cpf_cnpj?.length > 14
      )
    }

    // Filtrar por receita
    if (filters.minRevenue && filtered.top_clients) {
      filtered.top_clients = filtered.top_clients.filter(client =>
        parseFloat(client.total_revenue || 0) >= parseFloat(filters.minRevenue)
      )
    }

    if (filters.maxRevenue && filtered.top_clients) {
      filtered.top_clients = filtered.top_clients.filter(client =>
        parseFloat(client.total_revenue || 0) <= parseFloat(filters.maxRevenue)
      )
    }

    return filtered
  }

  // Aplicar filtros aos dados exibidos
  const filteredDashboardData = getFilteredData(dashboardData)

  // Funções de Comparação Temporal
  const calculatePercentageChange = (current, previous) => {
    if (!previous || previous === 0) return null
    const change = ((current - previous) / previous) * 100
    return change.toFixed(1)
  }

  const simulatePreviousPeriodData = (currentData) => {
    if (!currentData?.kpis) return null

    // Simular dados do período anterior (redução de 5-15% para demo)
    const variation = 0.85 + (Math.random() * 0.10) // 85% a 95% do valor atual

    return {
      kpis: {
        total_vehicles: Math.floor((currentData.kpis.total_vehicles || 0) * variation),
        active_contracts: Math.floor((currentData.kpis.active_contracts || 0) * variation),
        active_clients: Math.floor((currentData.kpis.active_clients || 0) * variation),
        revenue_period: (currentData.kpis.revenue_period || 0) * variation
      }
    }
  }

  // Gerar dados simulados do período anterior
  const previousData = simulatePreviousPeriodData(dashboardData)

  const getComparisonData = (currentValue, previousValue) => {
    const change = calculatePercentageChange(currentValue, previousValue)
    if (change === null) return null

    return {
      value: change,
      isPositive: parseFloat(change) >= 0,
      icon: parseFloat(change) >= 0 ? '▲' : '▼',
      color: parseFloat(change) >= 0 ? 'text-green-600' : 'text-red-600'
    }
  }

  // Funções de Alertas Inteligentes
  const checkIntelligentAlerts = () => {
    if (!dashboardData?.kpis) return

    const alerts = []
    const kpis = dashboardData.kpis

    // Alerta 1: Utilização de Veículos Baixa
    if (kpis.utilization_rate && kpis.utilization_rate < thresholds.vehicleUtilizationMin) {
      alerts.push({
        id: 'low-utilization',
        type: 'warning',
        icon: '🚛',
        title: 'Taxa de Utilização Abaixo do Ideal',
        description: `Apenas ${kpis.utilization_rate}% dos veículos estão em uso (mínimo: ${thresholds.vehicleUtilizationMin}%)`,
        action: 'Revisar estratégia de precificação ou marketing',
        severity: 'medium',
        time: 'agora'
      })
    }

    // Alerta 2: Poucos Contratos Ativos
    if (kpis.active_contracts && kpis.active_contracts < thresholds.contractsMin) {
      alerts.push({
        id: 'low-contracts',
        type: 'urgent',
        icon: '📋',
        title: 'Baixo Número de Contratos Ativos',
        description: `Apenas ${kpis.active_contracts} contratos ativos (mínimo recomendado: ${thresholds.contractsMin})`,
        action: 'Intensificar ações comerciais',
        severity: 'high',
        time: 'agora'
      })
    }

    // Alerta 3: Queda Acentuada na Receita
    if (previousData?.kpis) {
      const revenueChange = calculatePercentageChange(kpis.revenue_period, previousData.kpis.revenue_period)
      if (revenueChange && parseFloat(revenueChange) < thresholds.revenueDropPercent) {
        alerts.push({
          id: 'revenue-drop',
          type: 'urgent',
          icon: '💰',
          title: 'Queda Significativa na Receita',
          description: `Receita caiu ${Math.abs(parseFloat(revenueChange))}% em relação ao período anterior`,
          action: 'Analisar causa da queda e tomar medidas corretivas',
          severity: 'critical',
          time: 'agora'
        })
      }
    }

    // Alerta 4: Score Médio de Clientes Baixo
    if (kpis.avg_client_score && kpis.avg_client_score < thresholds.clientScoreMin) {
      alerts.push({
        id: 'low-client-score',
        type: 'warning',
        icon: '👥',
        title: 'Score Médio de Clientes Abaixo do Ideal',
        description: `Score médio atual: ${kpis.avg_client_score} (mínimo: ${thresholds.clientScoreMin})`,
        action: 'Revisar política de crédito e inadimplência',
        severity: 'medium',
        time: 'agora'
      })
    }

    // Alerta 5: Alta Taxa de Veículos Ociosos
    if (kpis.total_vehicles && kpis.utilization_rate) {
      const idleVehicles = Math.round(kpis.total_vehicles * (1 - kpis.utilization_rate / 100))
      if (idleVehicles >= 5) {
        alerts.push({
          id: 'high-idle-vehicles',
          type: 'info',
          icon: '⚠️',
          title: `${idleVehicles} Veículos Ociosos`,
          description: `${idleVehicles} veículos não estão gerando receita`,
          action: 'Considerar aluguel temporário ou venda',
          severity: 'low',
          time: 'agora'
        })
      }
    }

    setIntelligentAlerts(alerts)
  }

  const dismissAlert = (alertId) => {
    setIntelligentAlerts(prev => prev.filter(alert => alert.id !== alertId))
  }

  // Funções de Benchmarking
  const calculateBenchmarks = () => {
    if (!dashboardData?.top_clients || !dashboardData?.top_vehicles) return

    const topClients = dashboardData.top_clients || []
    const topVehicles = dashboardData.top_vehicles || []

    // Calcular médias da frota
    const avgClientRevenue = topClients.length > 0
      ? topClients.reduce((sum, c) => sum + parseFloat(c.total_revenue || 0), 0) / topClients.length
      : 0

    const avgClientContracts = topClients.length > 0
      ? topClients.reduce((sum, c) => sum + parseInt(c.active_contracts || 0), 0) / topClients.length
      : 0

    const avgVehicleRevenue = topVehicles.length > 0
      ? topVehicles.reduce((sum, v) => sum + parseFloat(v.total_revenue || 0), 0) / topVehicles.length
      : 0

    const avgVehicleRentals = topVehicles.length > 0
      ? topVehicles.reduce((sum, v) => sum + parseInt(v.total_rentals || 0), 0) / topVehicles.length
      : 0

    setBenchmarkData({
      avgClientRevenue,
      avgClientContracts,
      avgVehicleRevenue,
      avgVehicleRentals
    })
  }

  const getBenchmarkIndicator = (value, average, isHigherBetter = true) => {
    if (!average || average === 0) return null

    const percentageDiff = ((value - average) / average) * 100
    const isAboveAverage = value > average

    // Determinar se é positivo ou negativo baseado em isHigherBetter
    const isPositive = isHigherBetter ? isAboveAverage : !isAboveAverage

    return {
      percentageDiff: Math.abs(percentageDiff).toFixed(1),
      isAboveAverage,
      isPositive,
      icon: isAboveAverage ? '▲' : '▼',
      color: isPositive ? 'text-green-600' : 'text-red-600',
      text: isAboveAverage ? 'acima da média' : 'abaixo da média'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Carregando dashboard...</p>
        </div>
      </div>
    )
  }

  const kpis = dashboardData?.kpis || {}

  return (
    <div id="dashboard-container" className="space-y-6">
      {/* ABAS HORIZONTAIS */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-1.5">
        <div className="flex gap-1 overflow-x-auto">
          <TabItem icon="🏠" label="Principal" isActive={activeTab === 'principal'} onClick={() => setActiveTab('principal')} />
          <TabItem icon="💰" label="Financeiro" isActive={activeTab === 'financeiro'} onClick={() => setActiveTab('financeiro')} />
          <TabItem icon="🚛" label="Operacional" isActive={activeTab === 'operacional'} onClick={() => setActiveTab('operacional')} />
          <TabItem icon="📈" label="Analytics & BI" isActive={activeTab === 'analytics'} onClick={() => setActiveTab('analytics')} />
          <TabItem icon="👥" label="CRM" isActive={activeTab === 'crm'} onClick={() => setActiveTab('crm')} />
        </div>
      </div>

      {/* CONTEÚDO BASEADO NA ABA */}
      {activeTab === 'principal' && (
        <>
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                📊 Dashboard BI - Olá, {user?.name}! 👋
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Inteligência de negócio e análise completa da sua operação
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-4 sm:mt-0">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="form-input text-sm"
              >
                <option value="7d">Últimos 7 dias</option>
                <option value="30d">Últimos 30 dias</option>
                <option value="90d">Últimos 90 dias</option>
                <option value="1y">Último ano</option>
              </select>

              {/* Botões de Exportação */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={handleExportExcel}
                  className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors flex items-center gap-1.5"
                  title="Exportar dados para Excel"
                >
                  📊 <span className="hidden sm:inline">Excel</span>
                </button>

                <button
                  onClick={handleExportPDF}
                  className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors flex items-center gap-1.5"
                  title="Exportar dashboard para PDF"
                >
                  📄 <span className="hidden sm:inline">PDF</span>
                </button>

                <button
                  onClick={handleExportComplete}
                  className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg transition-colors flex items-center gap-1.5"
                  title="Exportar dashboard completo (Excel + PDF)"
                >
                  💾 <span className="hidden sm:inline">Completo</span>
                </button>
              </div>

              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={`px-3 py-2 text-sm rounded-lg transition-colors flex items-center gap-1.5 ${
                  showAdvancedFilters
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
                title="Abrir/Fechar filtros avançados"
              >
                🔍 <span className="hidden sm:inline">Filtros</span>
              </button>

              <button onClick={loadDashboardData} className="btn btn-primary text-sm">
                🔄 <span className="hidden sm:inline">Atualizar</span>
              </button>
            </div>
          </div>

          {/* Painel de Filtros Avançados */}
          {showAdvancedFilters && (
            <div className="card bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 border-2 border-blue-200 dark:border-blue-800">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <span>🔍</span> Filtros Avançados
                </h3>
                <button
                  onClick={clearFilters}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Limpar Filtros
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Filtro de Data */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Data Início
                  </label>
                  <input
                    type="date"
                    value={filters.dateStart}
                    onChange={(e) => handleFilterChange('dateStart', e.target.value)}
                    className="form-input w-full text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Data Fim
                  </label>
                  <input
                    type="date"
                    value={filters.dateEnd}
                    onChange={(e) => handleFilterChange('dateEnd', e.target.value)}
                    className="form-input w-full text-sm"
                  />
                </div>

                {/* Filtro de Tipo de Cliente */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tipo de Cliente
                  </label>
                  <select
                    value={filters.clientType}
                    onChange={(e) => handleFilterChange('clientType', e.target.value)}
                    className="form-input w-full text-sm"
                  >
                    <option value="all">Todos</option>
                    <option value="pf">Pessoa Física</option>
                    <option value="pj">Pessoa Jurídica</option>
                  </select>
                </div>

                {/* Filtro de Status de Contrato */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status do Contrato
                  </label>
                  <select
                    value={filters.contractStatus}
                    onChange={(e) => handleFilterChange('contractStatus', e.target.value)}
                    className="form-input w-full text-sm"
                  >
                    <option value="all">Todos</option>
                    <option value="active">Ativos</option>
                    <option value="inactive">Inativos</option>
                    <option value="expired">Expirados</option>
                  </select>
                </div>

                {/* Filtro de Receita Mínima */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Receita Mínima (R$)
                  </label>
                  <input
                    type="number"
                    value={filters.minRevenue}
                    onChange={(e) => handleFilterChange('minRevenue', e.target.value)}
                    placeholder="0.00"
                    className="form-input w-full text-sm"
                  />
                </div>

                {/* Filtro de Receita Máxima */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Receita Máxima (R$)
                  </label>
                  <input
                    type="number"
                    value={filters.maxRevenue}
                    onChange={(e) => handleFilterChange('maxRevenue', e.target.value)}
                    placeholder="999999.00"
                    className="form-input w-full text-sm"
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={() => setShowAdvancedFilters(false)}
                  className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors text-sm"
                >
                  Cancelar
                </button>
                <button
                  onClick={applyFilters}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center gap-2"
                >
                  <span>✓</span> Aplicar Filtros
                </button>
              </div>
            </div>
          )}

          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <KPICard
              title="Total de Veículos"
              value={kpis.total_vehicles || 0}
              change={`${kpis.utilization_rate || 0}% em uso`}
              changeType="positive"
              icon="🚛"
              color="blue"
              comparison={showComparison && previousData ? getComparisonData(kpis.total_vehicles, previousData.kpis.total_vehicles) : null}
            />
            <KPICard
              title="Contratos Ativos"
              value={kpis.active_contracts || 0}
              change="em operação"
              changeType="positive"
              icon="📋"
              color="green"
              comparison={showComparison && previousData ? getComparisonData(kpis.active_contracts, previousData.kpis.active_contracts) : null}
            />
            <KPICard
              title="Clientes Ativos"
              value={kpis.active_clients || 0}
              change={`Score médio: ${kpis.avg_client_score || 0}`}
              changeType="positive"
              icon="👥"
              color="purple"
              comparison={showComparison && previousData ? getComparisonData(kpis.active_clients, previousData.kpis.active_clients) : null}
            />
            <KPICard
              title="Receita no Período"
              value={`R$ ${(kpis.revenue_period || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
              change={selectedPeriod === '30d' ? 'últimos 30 dias' : selectedPeriod === '7d' ? 'últimos 7 dias' : 'no período'}
              changeType="positive"
              icon="💰"
              color="yellow"
              comparison={showComparison && previousData ? getComparisonData(kpis.revenue_period, previousData.kpis.revenue_period) : null}
            />
          </div>

          {/* Alertas Inteligentes */}
          {showAlerts && intelligentAlerts.length > 0 && (
            <div className="card bg-gradient-to-r from-orange-50 to-red-50 dark:from-gray-800 dark:to-gray-900 border-2 border-orange-200 dark:border-orange-800">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <span>🔔</span> Alertas Inteligentes
                  <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                    {intelligentAlerts.length}
                  </span>
                </h3>
                <button
                  onClick={() => setShowAlerts(false)}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  title="Ocultar alertas"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-3">
                {intelligentAlerts.map(alert => (
                  <div
                    key={alert.id}
                    className={`border-l-4 p-4 rounded-lg ${
                      alert.type === 'urgent'
                        ? 'border-l-red-500 bg-red-50 dark:bg-red-900/20'
                        : alert.type === 'warning'
                        ? 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'
                        : 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <span className="text-2xl">{alert.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {alert.title}
                            </p>
                            {alert.severity === 'critical' && (
                              <span className="px-2 py-0.5 bg-red-600 text-white text-xs rounded-full font-bold">
                                CRÍTICO
                              </span>
                            )}
                            {alert.severity === 'high' && (
                              <span className="px-2 py-0.5 bg-orange-600 text-white text-xs rounded-full font-bold">
                                ALTO
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                            {alert.description}
                          </p>
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                              💡 Ação recomendada:
                            </span>
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              {alert.action}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                            {alert.time}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => dismissAlert(alert.id)}
                        className="ml-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        title="Dispensar alerta"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center">
                Os alertas são gerados automaticamente com base nos thresholds configurados
              </div>
            </div>
          )}

          {/* Aprovações Pendentes de Política de Crédito */}
          <PendingApprovalsWidget />

          {/* Métricas de Análise de Crédito */}
          <CreditMetricsWidget />

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Chart */}
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">📈 Receita vs Contratos</h3>
                <div className="flex items-center space-x-2">
                  <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">Receita</span>
                  <span className="w-3 h-3 bg-green-500 rounded-full ml-4"></span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">Contratos</span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={dashboardData?.revenue_chart || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value, name) => [
                    name === 'revenue' ? `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : value,
                    name === 'revenue' ? 'Receita' : 'Contratos'
                  ]} />
                  <Area type="monotone" dataKey="revenue" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.1} />
                  <Line type="monotone" dataKey="contracts" stroke="#22c55e" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Vehicle Status */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">🚛 Status da Frota</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Disponíveis', value: kpis.available_vehicles || 0, color: '#22c55e' },
                      { name: 'Em Uso', value: kpis.rented_vehicles || 0, color: '#3b82f6' },
                      { name: 'Manutenção', value: kpis.maintenance_vehicles || 0, color: '#f59e0b' },
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {[
                      { name: 'Disponíveis', value: kpis.available_vehicles || 0, color: '#22c55e' },
                      { name: 'Em Uso', value: kpis.rented_vehicles || 0, color: '#3b82f6' },
                      { name: 'Manutenção', value: kpis.maintenance_vehicles || 0, color: '#f59e0b' },
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2">
                {[
                  { name: 'Disponíveis', value: kpis.available_vehicles || 0, color: '#22c55e' },
                  { name: 'Em Uso', value: kpis.rented_vehicles || 0, color: '#3b82f6' },
                  { name: 'Manutenção', value: kpis.maintenance_vehicles || 0, color: '#f59e0b' },
                ].map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">{item.name}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tables Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Clients */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">🏆 Top Clientes</h3>
                {showBenchmarking && benchmarkData && (
                  <button
                    onClick={() => setShowBenchmarking(!showBenchmarking)}
                    className="text-xs px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-lg"
                    title="Toggle benchmarking"
                  >
                    📊 Benchmark
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {(dashboardData?.top_clients || []).slice(0, 4).map((client, index) => {
                  const revenueBenchmark = showBenchmarking && benchmarkData
                    ? getBenchmarkIndicator(parseFloat(client.total_spent || 0), benchmarkData.avgClientRevenue)
                    : null
                  const contractsBenchmark = showBenchmarking && benchmarkData
                    ? getBenchmarkIndicator(parseInt(client.total_contracts || 0), benchmarkData.avgClientContracts)
                    : null

                  return (
                    <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{client.name}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Score: {client.grade} ({client.score})</p>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900 dark:text-white">
                              R$ {client.total_spent.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </p>
                            {revenueBenchmark && (
                              <span className={`text-xs font-semibold ${revenueBenchmark.color}`}>
                                {revenueBenchmark.icon} {revenueBenchmark.percentageDiff}%
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 justify-end">
                            <p className="text-sm text-gray-600 dark:text-gray-400">{client.total_contracts} contratos</p>
                            {contractsBenchmark && (
                              <span className={`text-xs font-semibold ${contractsBenchmark.color}`}>
                                {contractsBenchmark.icon}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {showBenchmarking && (revenueBenchmark || contractsBenchmark) && (
                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {revenueBenchmark && `Receita ${revenueBenchmark.text}`}
                            {revenueBenchmark && contractsBenchmark && ' • '}
                            {contractsBenchmark && `Contratos ${contractsBenchmark.text}`}
                          </p>
                        </div>
                      )}
                    </div>
                  )
                })}
                {(!dashboardData?.top_clients || dashboardData.top_clients.length === 0) && (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-4">Nenhum cliente cadastrado ainda</p>
                )}
              </div>
            </div>

            {/* Alerts */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">🚨 Alertas & Notificações</h3>
              <div className="space-y-3">
                <AlertItem type="urgent" icon="⚠️" title="Manutenção Urgente" description="Caminhão DAF-2156 precisa de revisão imediata" time="há 5 min" />
                <AlertItem type="warning" icon="⏰" title="Contrato Expirando" description="Construtora ABC - expira em 7 dias" time="há 2h" />
                <AlertItem type="success" icon="✅" title="Pagamento Recebido" description="R$ 45.000 - Obra Construtora XYZ" time="há 4h" />
              </div>
            </div>
          </div>

          {/* AI Predictions */}
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">🔮 Predições IA & Insights</h3>
              <span className="badge bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300">IA Ativa</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="text-2xl mb-2">📈</div>
                <h4 className="font-semibold text-gray-900 dark:text-white">Demanda Prevista</h4>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  +{dashboardData?.predictions?.demand_forecast?.next_month || 0}%
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">próximo mês</p>
              </div>
              <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="text-2xl mb-2">💰</div>
                <h4 className="font-semibold text-gray-900 dark:text-white">Receita Projetada</h4>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  R$ {((dashboardData?.predictions?.revenue_projection || 0) / 1000).toFixed(0)}k
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">próximo mês</p>
              </div>
              <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <div className="text-2xl mb-2">⚠️</div>
                <h4 className="font-semibold text-gray-900 dark:text-white">Risco de Churn</h4>
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  {dashboardData?.predictions?.churn_risk || 0} clientes
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">precisam atenção</p>
              </div>
            </div>
          </div>

          {/* Benchmarking Panel */}
          {showBenchmarking && benchmarkData && (
            <div className="card bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 border-2 border-purple-200 dark:border-purple-800">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <span>📊</span> Benchmarking da Frota
                  <span className="ml-2 px-2 py-0.5 bg-purple-500 text-white text-xs rounded-full">
                    Médias
                  </span>
                </h3>
                <button
                  onClick={() => setShowBenchmarking(false)}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  title="Ocultar benchmarking"
                >
                  ✕
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Média de Receita por Cliente */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">💰</span>
                    <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Receita Média/Cliente
                    </h4>
                  </div>
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    R$ {benchmarkData.avgClientRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Baseado em {dashboardData?.top_clients?.length || 0} clientes
                  </p>
                </div>

                {/* Média de Contratos por Cliente */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">📋</span>
                    <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Contratos Médios/Cliente
                    </h4>
                  </div>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {benchmarkData.avgClientContracts.toFixed(1)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Média de contratos ativos
                  </p>
                </div>

                {/* Média de Receita por Veículo */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🚛</span>
                    <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Receita Média/Veículo
                    </h4>
                  </div>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    R$ {benchmarkData.avgVehicleRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Baseado em {dashboardData?.top_vehicles?.length || 0} veículos
                  </p>
                </div>

                {/* Média de Aluguéis por Veículo */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">📊</span>
                    <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Aluguéis Médios/Veículo
                    </h4>
                  </div>
                  <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                    {benchmarkData.avgVehicleRentals.toFixed(1)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Número médio de aluguéis
                  </p>
                </div>
              </div>

              <div className="mt-4 p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <span className="text-lg">💡</span>
                  <div>
                    <h5 className="text-sm font-semibold text-purple-900 dark:text-purple-300 mb-1">
                      Como usar o Benchmarking
                    </h5>
                    <p className="text-xs text-purple-800 dark:text-purple-400">
                      Os indicadores <span className="font-bold text-green-600">▲ verdes</span> mostram valores acima da média (melhor performance),
                      enquanto <span className="font-bold text-red-600">▼ vermelhos</span> indicam abaixo da média (requer atenção).
                      Use essas métricas para identificar clientes e veículos de alta performance e aqueles que precisam de melhorias.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Dashboard Analytics & BI */}
      {activeTab === 'analytics' && (
        <>
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              📈 Analytics & BI
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Relatórios avançados e inteligência de negócio
            </p>
          </div>

          {/* KPIs Analytics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <KPICard
              title="Taxa de Conversão"
              value={`${((dashboardData?.analytics?.conversion_rate || 0) * 100).toFixed(1)}%`}
              change="+2.3% vs mês anterior"
              changeType="positive"
              icon="🎯"
              color="blue"
            />
            <KPICard
              title="Lifetime Value (LTV)"
              value={`R$ ${(dashboardData?.analytics?.avg_ltv || 0).toLocaleString('pt-BR')}`}
              change="+15% vs trimestre"
              changeType="positive"
              icon="💎"
              color="purple"
            />
            <KPICard
              title="ROI Marketing"
              value={`${((dashboardData?.analytics?.marketing_roi || 0) * 100).toFixed(0)}%`}
              change="Retorno sobre investimento"
              changeType="positive"
              icon="📊"
              color="green"
            />
            <KPICard
              title="Churn Rate"
              value={`${((dashboardData?.analytics?.churn_rate || 0) * 100).toFixed(1)}%`}
              change={dashboardData?.analytics?.churn_rate > 0.05 ? 'Acima da meta' : 'Dentro da meta'}
              changeType={dashboardData?.analytics?.churn_rate > 0.05 ? 'negative' : 'positive'}
              icon="🔄"
              color="yellow"
            />
          </div>

          {/* Advanced Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Cohort Analysis */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">📅 Análise de Coorte - Retenção</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dashboardData?.analytics?.cohort_data || [
                  { month: 'Jan', retention: 100 },
                  { month: 'Fev', retention: 85 },
                  { month: 'Mar', retention: 70 },
                  { month: 'Abr', retention: 65 },
                  { month: 'Mai', retention: 60 },
                  { month: 'Jun', retention: 58 }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip formatter={(value) => [`${value}%`, 'Retenção']} />
                  <Line type="monotone" dataKey="retention" stroke="#8b5cf6" strokeWidth={3} dot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Performance por Segmento */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">🏢 Performance por Segmento</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dashboardData?.analytics?.segment_performance || [
                  { segment: 'Construção', revenue: 450000, clients: 28, avg_contract: 16071 },
                  { segment: 'Logística', revenue: 320000, clients: 45, avg_contract: 7111 },
                  { segment: 'Agro', revenue: 280000, clients: 22, avg_contract: 12727 },
                  { segment: 'Mineração', revenue: 150000, clients: 8, avg_contract: 18750 }
                ]} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="segment" type="category" width={80} />
                  <Tooltip formatter={(value, name) => [
                    name === 'revenue' ? `R$ ${value.toLocaleString('pt-BR')}` : value,
                    name === 'revenue' ? 'Receita' : name === 'clients' ? 'Clientes' : 'Contrato Médio'
                  ]} />
                  <Bar dataKey="revenue" fill="#3b82f6" name="revenue" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Predictive Analytics */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">🔮 Análises Preditivas</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-blue-600 rounded-lg">
                    <span className="text-white text-xl">📈</span>
                  </div>
                  <h4 className="ml-3 font-semibold text-gray-900 dark:text-white">Demanda Futura</h4>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Próximos 30 dias</span>
                    <span className="font-bold text-blue-600 dark:text-blue-400">+23%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Próximos 60 dias</span>
                    <span className="font-bold text-blue-600 dark:text-blue-400">+35%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Próximos 90 dias</span>
                    <span className="font-bold text-blue-600 dark:text-blue-400">+28%</span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-purple-600 rounded-lg">
                    <span className="text-white text-xl">🎯</span>
                  </div>
                  <h4 className="ml-3 font-semibold text-gray-900 dark:text-white">Clientes em Risco</h4>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Alto risco</span>
                    <span className="font-bold text-red-600 dark:text-red-400">{dashboardData?.analytics?.high_risk_clients || 3}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Médio risco</span>
                    <span className="font-bold text-yellow-600 dark:text-yellow-400">{dashboardData?.analytics?.medium_risk_clients || 8}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Baixo risco</span>
                    <span className="font-bold text-green-600 dark:text-green-400">{dashboardData?.analytics?.low_risk_clients || 42}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-green-600 rounded-lg">
                    <span className="text-white text-xl">💰</span>
                  </div>
                  <h4 className="ml-3 font-semibold text-gray-900 dark:text-white">Oportunidades</h4>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Upsell potencial</span>
                    <span className="font-bold text-green-600 dark:text-green-400">R$ 127k</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Cross-sell</span>
                    <span className="font-bold text-green-600 dark:text-green-400">R$ 85k</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Novos clientes</span>
                    <span className="font-bold text-green-600 dark:text-green-400">R$ 310k</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Relatórios Exportáveis */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">📄 Relatórios Executivos</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 transition-colors group">
                <div className="text-center">
                  <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">📊</div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">Relatório Mensal</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Performance completa</p>
                </div>
              </button>
              <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-green-500 dark:hover:border-green-400 transition-colors group">
                <div className="text-center">
                  <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">💰</div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">DRE Detalhado</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Demonstrativo financeiro</p>
                </div>
              </button>
              <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-purple-500 dark:hover:border-purple-400 transition-colors group">
                <div className="text-center">
                  <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">🎯</div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">Análise de Clientes</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Segmentação e scoring</p>
                </div>
              </button>
              <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-yellow-500 dark:hover:border-yellow-400 transition-colors group">
                <div className="text-center">
                  <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">🚛</div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">Eficiência da Frota</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Utilização e custos</p>
                </div>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Dashboard Financeiro */}
      {activeTab === 'financeiro' && (
        <>
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              💰 Dashboard Financeiro
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Fluxo de caixa, DRE e gestão financeira completa
            </p>
          </div>

          {/* KPIs Financeiros */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <KPICard
              title="Faturamento Mensal"
              value={`R$ ${(dashboardData?.financial?.monthly_revenue || 245000).toLocaleString('pt-BR')}`}
              change="+12% vs mês anterior"
              changeType="positive"
              icon="💰"
              color="green"
            />
            <KPICard
              title="Margem Bruta"
              value={`${((dashboardData?.financial?.gross_margin || 0.68) * 100).toFixed(1)}%`}
              change="Meta: 65%"
              changeType="positive"
              icon="📊"
              color="blue"
            />
            <KPICard
              title="Contas a Receber"
              value={`R$ ${(dashboardData?.financial?.accounts_receivable || 127000).toLocaleString('pt-BR')}`}
              change="Prazo médio: 35 dias"
              changeType="positive"
              icon="📥"
              color="purple"
            />
            <KPICard
              title="Fluxo de Caixa"
              value={`R$ ${(dashboardData?.financial?.cash_flow || 89000).toLocaleString('pt-BR')}`}
              change={dashboardData?.financial?.cash_flow > 0 ? 'Positivo' : 'Negativo'}
              changeType={dashboardData?.financial?.cash_flow > 0 ? 'positive' : 'negative'}
              icon="🏦"
              color="yellow"
            />
          </div>

          {/* Charts Financeiros */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Fluxo de Caixa */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">💸 Fluxo de Caixa (12 meses)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={dashboardData?.financial?.cashflow_chart || [
                  { month: 'Jan', inflow: 180000, outflow: 120000, net: 60000 },
                  { month: 'Fev', inflow: 195000, outflow: 135000, net: 60000 },
                  { month: 'Mar', inflow: 220000, outflow: 140000, net: 80000 },
                  { month: 'Abr', inflow: 210000, outflow: 145000, net: 65000 },
                  { month: 'Mai', inflow: 235000, outflow: 150000, net: 85000 },
                  { month: 'Jun', inflow: 245000, outflow: 156000, net: 89000 }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value, name) => [
                    `R$ ${value.toLocaleString('pt-BR')}`,
                    name === 'inflow' ? 'Entrada' : name === 'outflow' ? 'Saída' : 'Líquido'
                  ]} />
                  <Area type="monotone" dataKey="inflow" stackId="1" stroke="#22c55e" fill="#22c55e" fillOpacity={0.3} />
                  <Area type="monotone" dataKey="outflow" stackId="2" stroke="#ef4444" fill="#ef4444" fillOpacity={0.3} />
                  <Line type="monotone" dataKey="net" stroke="#3b82f6" strokeWidth={3} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* DRE Resumida */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">📋 DRE - Últimos 6 Meses</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dashboardData?.financial?.dre_chart || [
                  { month: 'Jan', receita: 180000, custo: 72000, lucro: 54000 },
                  { month: 'Fev', receita: 195000, custo: 78000, lucro: 58500 },
                  { month: 'Mar', receita: 220000, custo: 88000, lucro: 66000 },
                  { month: 'Abr', receita: 210000, custo: 84000, lucro: 63000 },
                  { month: 'Mai', receita: 235000, custo: 94000, lucro: 70500 },
                  { month: 'Jun', receita: 245000, custo: 98000, lucro: 73500 }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value, name) => [
                    `R$ ${value.toLocaleString('pt-BR')}`,
                    name === 'receita' ? 'Receita' : name === 'custo' ? 'Custo' : 'Lucro'
                  ]} />
                  <Bar dataKey="receita" fill="#3b82f6" name="receita" />
                  <Bar dataKey="custo" fill="#ef4444" name="custo" />
                  <Bar dataKey="lucro" fill="#22c55e" name="lucro" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Análises Financeiras */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Contas a Receber por Vencimento */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">📅 Contas a Receber por Vencimento</h3>
              <div className="space-y-4">
                {[
                  { period: 'A vencer (0-30 dias)', amount: 85000, percentage: 67, color: 'bg-green-500' },
                  { period: 'Vencidas (31-60 dias)', amount: 25000, percentage: 20, color: 'bg-yellow-500' },
                  { period: 'Vencidas (61-90 dias)', amount: 12000, percentage: 9, color: 'bg-orange-500' },
                  { period: 'Vencidas (+90 dias)', amount: 5000, percentage: 4, color: 'bg-red-500' }
                ].map((item, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.period}</span>
                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                        R$ {item.amount.toLocaleString('pt-BR')}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className={`${item.color} h-2 rounded-full transition-all duration-500`} style={{ width: `${item.percentage}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Indicadores Financeiros */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">📊 Indicadores Financeiros</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {((dashboardData?.financial?.net_margin || 0.30) * 100).toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Margem Líquida</div>
                </div>
                <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {(dashboardData?.financial?.roa || 0.18 * 100).toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">ROA</div>
                </div>
                <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {(dashboardData?.financial?.current_ratio || 2.3).toFixed(1)}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Liquidez Corrente</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                    {(dashboardData?.financial?.debt_ratio || 0.35 * 100).toFixed(0)}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Endividamento</div>
                </div>
              </div>
            </div>
          </div>

          {/* Ações Rápidas Financeiras */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">⚡ Ações Rápidas</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-green-500 dark:hover:border-green-400 transition-colors group">
                <div className="text-center">
                  <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">💳</div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">Contas a Receber</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Gerenciar cobranças</p>
                </div>
              </button>
              <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-red-500 dark:hover:border-red-400 transition-colors group">
                <div className="text-center">
                  <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">💸</div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">Contas a Pagar</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Programar pagamentos</p>
                </div>
              </button>
              <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 transition-colors group">
                <div className="text-center">
                  <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">📊</div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">Relatório DRE</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Gerar demonstrativo</p>
                </div>
              </button>
              <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-purple-500 dark:hover:border-purple-400 transition-colors group">
                <div className="text-center">
                  <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">📈</div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">Fluxo de Caixa</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Projeção 90 dias</p>
                </div>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Dashboard Operacional */}
      {activeTab === 'operacional' && (
        <>
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              🚛 Dashboard Operacional
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Gestão de frota, manutenções e operações
            </p>
          </div>

          {/* KPIs Operacionais */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <KPICard
              title="Frota Total"
              value={dashboardData?.operational?.total_vehicles || 45}
              change={`${dashboardData?.operational?.utilization_rate || 78}% utilizados`}
              changeType="positive"
              icon="🚛"
              color="blue"
            />
            <KPICard
              title="Km Rodados (Mês)"
              value={`${(dashboardData?.operational?.monthly_km || 125000).toLocaleString('pt-BR')} km`}
              change="+8% vs mês anterior"
              changeType="positive"
              icon="🛣️"
              color="green"
            />
            <KPICard
              title="Eficiência Combustível"
              value={`${(dashboardData?.operational?.fuel_efficiency || 3.2).toFixed(1)} km/l`}
              change="Meta: 3.0 km/l"
              changeType="positive"
              icon="⛽"
              color="purple"
            />
            <KPICard
              title="Tempo Inatividade"
              value={`${(dashboardData?.operational?.downtime_hours || 45)} horas`}
              change={dashboardData?.operational?.downtime_hours < 50 ? 'Dentro da meta' : 'Acima da meta'}
              changeType={dashboardData?.operational?.downtime_hours < 50 ? 'positive' : 'negative'}
              icon="⏱️"
              color="yellow"
            />
          </div>

          {/* Charts Operacionais */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Utilização da Frota */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">📊 Utilização da Frota (7 dias)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={dashboardData?.operational?.utilization_chart || [
                  { day: 'Seg', utilizados: 32, disponiveis: 13, manutencao: 2 },
                  { day: 'Ter', utilizados: 35, disponiveis: 9, manutencao: 3 },
                  { day: 'Qua', utilizados: 38, disponiveis: 6, manutencao: 3 },
                  { day: 'Qui', utilizados: 34, disponiveis: 8, manutencao: 5 },
                  { day: 'Sex', utilizados: 40, disponiveis: 4, manutencao: 3 },
                  { day: 'Sab', utilizados: 25, disponiveis: 18, manutencao: 4 },
                  { day: 'Dom', utilizados: 15, disponiveis: 26, manutencao: 6 }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip formatter={(value, name) => [
                    value,
                    name === 'utilizados' ? 'Em Uso' : name === 'disponiveis' ? 'Disponíveis' : 'Manutenção'
                  ]} />
                  <Area type="monotone" dataKey="utilizados" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.7} />
                  <Area type="monotone" dataKey="disponiveis" stackId="1" stroke="#22c55e" fill="#22c55e" fillOpacity={0.7} />
                  <Area type="monotone" dataKey="manutencao" stackId="1" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.7} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Manutenções por Tipo */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">🔧 Manutenções por Tipo</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={dashboardData?.operational?.maintenance_types || [
                      { name: 'Preventiva', value: 65, color: '#22c55e' },
                      { name: 'Corretiva', value: 25, color: '#ef4444' },
                      { name: 'Preditiva', value: 10, color: '#3b82f6' }
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {(dashboardData?.operational?.maintenance_types || [
                      { name: 'Preventiva', value: 65, color: '#22c55e' },
                      { name: 'Corretiva', value: 25, color: '#ef4444' },
                      { name: 'Preditiva', value: 10, color: '#3b82f6' }
                    ]).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2">
                {(dashboardData?.operational?.maintenance_types || [
                  { name: 'Preventiva', value: 65, color: '#22c55e' },
                  { name: 'Corretiva', value: 25, color: '#ef4444' },
                  { name: 'Preditiva', value: 10, color: '#3b82f6' }
                ]).map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">{item.name}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{item.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Métricas Operacionais */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Performance de Motoristas */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">👨‍💼 Top Motoristas</h3>
              <div className="space-y-3">
                {(dashboardData?.operational?.top_drivers || [
                  { name: 'João Silva', score: 98, trips: 145, violations: 0 },
                  { name: 'Carlos Santos', score: 94, trips: 132, violations: 1 },
                  { name: 'Pedro Lima', score: 92, trips: 128, violations: 2 },
                  { name: 'Ana Costa', score: 89, trips: 118, violations: 3 }
                ]).slice(0, 4).map((driver, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                        index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-400' : 'bg-blue-500'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{driver.name}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{driver.trips} viagens</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg text-gray-900 dark:text-white">{driver.score}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{driver.violations} infrações</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Alertas Operacionais */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">🚨 Alertas Operacionais</h3>
              <div className="space-y-3">
                <AlertItem
                  type="urgent"
                  icon="🔧"
                  title="Manutenção Urgente"
                  description="Caminhão MBX-1234 - Problema no freio"
                  time="há 10 min"
                />
                <AlertItem
                  type="warning"
                  icon="⛽"
                  title="Consumo Elevado"
                  description="Volvo FH-5678 - 15% acima da média"
                  time="há 1h"
                />
                <AlertItem
                  type="info"
                  icon="📍"
                  title="Rota Otimizada"
                  description="Economia de 45min na rota SP-RJ"
                  time="há 2h"
                />
                <AlertItem
                  type="success"
                  icon="✅"
                  title="Manutenção Concluída"
                  description="Scania R500-9876 liberado"
                  time="há 3h"
                />
              </div>
            </div>

            {/* KPIs Rápidos */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">📈 KPIs Rápidos</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Pontualidade</p>
                    <p className="text-xl font-bold text-blue-600 dark:text-blue-400">96.5%</p>
                  </div>
                  <div className="text-2xl">⏰</div>
                </div>

                <div className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Satisfação Cliente</p>
                    <p className="text-xl font-bold text-green-600 dark:text-green-400">4.8/5</p>
                  </div>
                  <div className="text-2xl">😊</div>
                </div>

                <div className="flex justify-between items-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Custo por Km</p>
                    <p className="text-xl font-bold text-purple-600 dark:text-purple-400">R$ 2.85</p>
                  </div>
                  <div className="text-2xl">💰</div>
                </div>

                <div className="flex justify-between items-center p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Acidentes (mês)</p>
                    <p className="text-xl font-bold text-yellow-600 dark:text-yellow-400">0</p>
                  </div>
                  <div className="text-2xl">🛡️</div>
                </div>
              </div>
            </div>
          </div>

          {/* Ações Rápidas Operacionais */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">⚡ Ações Rápidas</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 transition-colors group">
                <div className="text-center">
                  <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">🚛</div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">Frota</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Gerenciar veículos</p>
                </div>
              </button>
              <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-orange-500 dark:hover:border-orange-400 transition-colors group">
                <div className="text-center">
                  <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">🔧</div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">Manutenções</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Agendar serviços</p>
                </div>
              </button>
              <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-green-500 dark:hover:border-green-400 transition-colors group">
                <div className="text-center">
                  <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">📍</div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">Rastreamento</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Localização GPS</p>
                </div>
              </button>
              <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-purple-500 dark:hover:border-purple-400 transition-colors group">
                <div className="text-center">
                  <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">📋</div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">Checklists</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Inspeções diárias</p>
                </div>
              </button>
              <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-red-500 dark:hover:border-red-400 transition-colors group">
                <div className="text-center">
                  <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">🚨</div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">Emergência</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Suporte 24h</p>
                </div>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Outros Dashboards - Placeholders */}
      {activeTab === 'crm' && <DashboardPlaceholder icon="👥" title="Dashboard CRM" subtitle="Pipeline de vendas, scoring clientes" />}
    </div>
  )
}

// Componentes
const TabItem = ({ icon, label, isActive, onClick }) => (
  <button onClick={onClick} className={`flex items-center gap-2.5 px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${isActive ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
    <span className="text-base">{icon}</span>
    <span>{label}</span>
  </button>
)

const DashboardPlaceholder = ({ icon, title, subtitle }) => (
  <div className="text-center py-12 card">
    <div className="text-4xl mb-4">{icon}</div>
    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
    <p className="text-gray-600 dark:text-gray-400">{subtitle}</p>
  </div>
)

const KPICard = ({ title, value, change, changeType, icon, color, comparison }) => {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400',
  }

  return (
    <div className="card">
      <div className="flex items-center">
        <div className={`p-3 rounded-lg ${colorClasses[color]} text-2xl`}>{icon}</div>
        <div className="ml-4 flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400 truncate">{title}</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white break-words">{value}</p>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <span className={`text-sm ${changeType === 'positive' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {change}
        </span>
        {comparison && (
          <div className={`flex items-center gap-1 text-sm font-semibold ${comparison.color}`}>
            <span className="text-lg">{comparison.icon}</span>
            <span>{comparison.value}%</span>
          </div>
        )}
      </div>
      {comparison && (
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          vs. período anterior
        </div>
      )}
    </div>
  )
}

const AlertItem = ({ type, icon, title, description, time }) => {
  const typeClasses = {
    urgent: 'border-l-red-500 bg-red-50 dark:bg-red-900/20',
    warning: 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
    success: 'border-l-green-500 bg-green-50 dark:bg-green-900/20',
    info: 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/20',
  }

  return (
    <div className={`border-l-4 p-3 rounded-lg ${typeClasses[type]}`}>
      <div className="flex items-start space-x-3">
        <span className="text-xl">{icon}</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 dark:text-white">{title}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{description}</p>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{time}</p>
        </div>
      </div>
    </div>
  )
}

export default DashboardPage